/**
 * @fileoverview Search history table — single combined view of every
 * InfoTrack order on a job (any status). Replaces the previous
 * "In progress" + "Completed" tabs.
 *
 * Columns: Product · Status · Requested by · Run date · Fee · Result · Actions
 *
 * - Status badge colour reflects lifecycle stage. Failed rows show an
 *   alert icon next to the badge; clicking it opens a popover with the
 *   parsed validation messages (and a collapsible raw payload for
 *   debugging) instead of dumping a raw JSON blob into the page.
 * - "Result" deep-links into the existing `DocumentDetail` sheet via the
 *   `onOpenDocument` callback (mounted at the page level so the same
 *   drawer is reused regardless of where the row is opened from).
 * - "Retry PDF download" appears for in-flight orders that have a remote
 *   order id but haven't yet downloaded the PDF.
 * - "Check for new filings" (only shown for products that wire change
 *   detection — currently ASIC company extracts) calls InfoTrack's
 *   `/api/document/lookup` to count documents filed since the extract.
 *   Result renders inline as "N new filings since this extract" with a
 *   one-click "Re-order" action; popover surfaces the most-recent filing
 *   date and a preview of the new documents. Products without a wired
 *   strategy just show "Purchased Xd ago" — no misleading "Up to date"
 *   reading from a flag InfoTrack doesn't actually return.
 * - "Delete" soft-deletes a row (hidden behind a "Show deleted" toggle so
 *   the audit trail isn't lost). Deleted rows can be restored.
 * - "Fee" shows InfoTrack's reported total fee for the order (AUD), when
 *   the order-status response included one.
 */

import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Switch } from "@/ui/primitives/switch";
import { Label } from "@/ui/primitives/label";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/primitives/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/ui/composed/overlays/dialog";
import {
  AlertCircle,
  ExternalLink,
  RefreshCw,
  RotateCcw,
  ShoppingCart,
  Sparkles,
  Trash2,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";

type ChangeCheckResult = {
  changed: boolean;
  changeCheckMethod:
    "infotrack_metadata" | "asic_document_lookup" | "unavailable";
  checkedAt: number;
  detail?: string;
  newDocumentCount?: number;
  latestDocumentDate?: string;
};

type OrderRow = {
  _id: Id<"infoTrackOrders">;
  _creationTime: number;
  productKey: string;
  status:
    | "draft"
    | "approved"
    | "submitted"
    | "in_progress"
    | "completed"
    | "failed"
    | "cancelled";
  infoTrackOrderId?: string;
  inputPayload: Record<string, unknown>;
  displayName: string;
  resultDocumentId?: Id<"documents">;
  /**
   * Labeled result documents (e.g. PPSR "Detailed report" / "Summary
   * report", one PDF per AML sub-report). When present, the result cell
   * renders one labeled link per document instead of the generic
   * "View document" link.
   */
  resultDocuments?: Array<{ documentId: Id<"documents">; label: string }>;
  completedAt?: number;
  /** InfoTrack-reported total fee (AUD cents), set at completion when available. */
  totalFeeAudCents?: number;
  errorMessage?: string;
  requesterName: string;
  lastChangeCheckAt?: number;
  lastChangeCheckResult?: ChangeCheckResult;
  supportsChangeCheck: boolean;
  /** Human-readable remote stage from the last poll (VOI/AML pending states). */
  lastRemoteStatus?: string;
  deletedAt?: number;
  deletedBy?: Id<"users">;
};

interface SearchesHistoryTableProps {
  jobId: Id<"jobs">;
  rows: OrderRow[];
  /**
   * Opens the DocumentDetail sheet (mounted at the page level) for the
   * given document id. Passed in by the parent route so a single sheet
   * instance handles every row in the table.
   */
  onOpenDocument: (documentId: Id<"documents">) => void;
}

const STATUS_LABELS: Record<OrderRow["status"], string> = {
  draft: "Draft",
  approved: "Approved",
  submitted: "Submitted",
  in_progress: "In progress",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
};

const STATUS_COLORS: Record<OrderRow["status"], BadgeColor> = {
  draft: "gray",
  approved: "blue",
  submitted: "blue",
  in_progress: "amber",
  completed: "green",
  failed: "red",
  cancelled: "gray",
};

function formatFeeAud(cents: number): string {
  return (cents / 100).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });
}

function formatRunDate(ms: number): string {
  return new Date(ms).toLocaleString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * "2m ago" / "3h ago" / "5d ago" / dd/MM/yyyy. Designed for the change-check
 * subtitle so the latest currency check reads naturally without taking up a
 * full timestamp's worth of horizontal space.
 */
function formatRelative(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 0) return "just now";
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Extracts a friendly summary from an InfoTrack-formatted error string.
 *
 * Backend stores errors as e.g.
 *   `InfoTrack request failed (400): {"errors":{"organisationNumber":[
 *    "A.C.N. ... is not a valid ABN or ACN."]},"title":"...","status":400}`
 *
 * We try to lift out the structured `errors` map (preferred) or `title`
 * field, falling back to the raw string when nothing parses.
 */
function parseInfotrackError(raw: string): {
  headline: string;
  bullets: string[];
  rawPayload?: string;
} {
  const fallback = { headline: raw, bullets: [] as string[] };
  const jsonStart = raw.indexOf("{");
  if (jsonStart === -1) return fallback;

  const jsonPart = raw.slice(jsonStart);
  const parsed: unknown = (() => {
    try {
      return JSON.parse(jsonPart) as unknown;
    } catch {
      return undefined;
    }
  })();
  if (!parsed || typeof parsed !== "object") return fallback;

  const obj = parsed as Record<string, unknown>;
  const prefix = raw.slice(0, jsonStart).trim().replace(/:\s*$/, "");
  const titleField = typeof obj.title === "string" ? obj.title : undefined;
  const headline = titleField || prefix || "InfoTrack request failed";

  const bullets: string[] = [];
  const errors = obj.errors;
  if (errors && typeof errors === "object" && !Array.isArray(errors)) {
    for (const [field, messages] of Object.entries(
      errors as Record<string, unknown>,
    )) {
      const list = Array.isArray(messages) ? messages : [messages];
      for (const m of list) {
        if (typeof m === "string") {
          bullets.push(field === "_" ? m : `${field}: ${m}`);
        }
      }
    }
  } else if (typeof obj.detail === "string") {
    bullets.push(obj.detail);
  }

  return {
    headline,
    bullets,
    rawPayload: jsonPart,
  };
}

export function SearchesHistoryTable({
  jobId,
  rows,
  onOpenDocument,
}: SearchesHistoryTableProps) {
  const retryDownload = useConvexMutation(
    api.jobs.searches.mutations.retryInfotrackOrderDownload,
  );
  const softDelete = useConvexMutation(
    api.jobs.searches.mutations.softDeleteInfotrackOrder,
  );
  const restore = useConvexMutation(
    api.jobs.searches.mutations.restoreInfotrackOrder,
  );
  const requestChangeCheck = useConvexMutation(
    api.jobs.searches.mutations.requestInfotrackChangeCheck,
  );
  const submitManualSearch = useConvexMutation(
    api.jobs.searches.mutations.submitManualInfotrackSearch,
  );

  const retryMutation = useMutation({
    mutationFn: (orderId: Id<"infoTrackOrders">) => retryDownload({ orderId }),
    onSuccess: () => toast.success("Download queued"),
    onError: (e: Error) =>
      toast.error(e.message || "Could not queue download."),
  });
  const deleteMutation = useMutation({
    mutationFn: (orderId: Id<"infoTrackOrders">) => softDelete({ orderId }),
    onSuccess: () => toast.success("Search deleted from view"),
    onError: (e: Error) => toast.error(e.message || "Could not delete."),
  });
  const restoreMutation = useMutation({
    mutationFn: (orderId: Id<"infoTrackOrders">) => restore({ orderId }),
    onSuccess: () => toast.success("Search restored"),
    onError: (e: Error) => toast.error(e.message || "Could not restore."),
  });
  const checkMutation = useMutation({
    mutationFn: (orderId: Id<"infoTrackOrders">) =>
      requestChangeCheck({ orderId }),
    onSuccess: () =>
      toast.success("Checking InfoTrack for new filings", {
        description: "Result will appear in the row in a few seconds.",
      }),
    onError: (e: Error) =>
      toast.error(e.message || "Could not check for updates."),
  });
  const reorderMutation = useMutation({
    mutationFn: (order: OrderRow) =>
      submitManualSearch({
        jobId,
        productKey: order.productKey,
        inputPayload: order.inputPayload,
      }),
    onSuccess: () => toast.success("New search submitted to InfoTrack"),
    onError: (e: Error) =>
      toast.error(e.message || "Could not submit re-order."),
  });

  const [showDeleted, setShowDeleted] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<OrderRow | null>(null);
  const [confirmReorder, setConfirmReorder] = useState<OrderRow | null>(null);

  // Drafts are an internal pre-submit state — practitioners should never
  // see them, so filter them out to keep the table focused on real orders.
  const realOrders = useMemo(
    () => rows.filter((r) => r.status !== "draft"),
    [rows],
  );
  const liveOrders = useMemo(
    () => realOrders.filter((r) => !r.deletedAt),
    [realOrders],
  );
  const deletedOrders = useMemo(
    () => realOrders.filter((r) => !!r.deletedAt),
    [realOrders],
  );

  const visible = showDeleted ? realOrders : liveOrders;

  if (realOrders.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">No searches yet</CardTitle>
          <CardDescription>
            Approved recommendations and manual orders will appear here once
            they're submitted to InfoTrack.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {deletedOrders.length > 0 ? (
        <div className="flex items-center justify-end gap-2">
          <Switch
            id="searches-show-deleted"
            checked={showDeleted}
            onCheckedChange={setShowDeleted}
          />
          <Label
            htmlFor="searches-show-deleted"
            className="text-xs text-muted-foreground"
          >
            Show deleted ({deletedOrders.length})
          </Label>
        </div>
      ) : null}

      <div className="rounded-lg border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Requested by</TableHead>
              <TableHead>Run date</TableHead>
              <TableHead>Fee</TableHead>
              <TableHead>Result</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((order) => {
              const isDeleted = !!order.deletedAt;
              const canRetry =
                !isDeleted &&
                order.status === "in_progress" &&
                !!order.infoTrackOrderId;
              const showResult =
                order.status === "completed" && !!order.resultDocumentId;
              const isFailed = order.status === "failed";

              return (
                <TableRow
                  key={order._id}
                  className={isDeleted ? "opacity-60" : undefined}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <span>{order.displayName}</span>
                      {isDeleted ? (
                        <Badge color="gray" variant="soft">
                          Deleted
                        </Badge>
                      ) : null}
                    </div>
                    {order.infoTrackOrderId ? (
                      <div className="text-xs text-muted-foreground">
                        Remote id {order.infoTrackOrderId}
                      </div>
                    ) : null}
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Badge color={STATUS_COLORS[order.status]} variant="soft">
                        {STATUS_LABELS[order.status]}
                      </Badge>
                      {isFailed && order.errorMessage ? (
                        <ErrorPopover error={order.errorMessage} />
                      ) : null}
                    </div>
                    {order.status === "in_progress" &&
                    order.lastRemoteStatus ? (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {order.lastRemoteStatus}
                      </div>
                    ) : null}
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {order.requesterName}
                  </TableCell>

                  <TableCell>
                    <div>{formatRunDate(order._creationTime)}</div>
                    {order.completedAt &&
                    order.completedAt !== order._creationTime ? (
                      <div className="text-xs text-muted-foreground">
                        Completed {formatRunDate(order.completedAt)}
                      </div>
                    ) : null}
                  </TableCell>

                  <TableCell className="text-sm">
                    {order.totalFeeAudCents !== undefined ? (
                      formatFeeAud(order.totalFeeAudCents)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  <TableCell>
                    {showResult ? (
                      <div className="space-y-1">
                        {(order.resultDocuments &&
                        order.resultDocuments.length > 0
                          ? order.resultDocuments
                          : [
                              {
                                documentId:
                                  order.resultDocumentId as Id<"documents">,
                                label: "View document",
                              },
                            ]
                        ).map((doc) => (
                          <div key={doc.documentId}>
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="h-auto p-0"
                              onClick={() => onOpenDocument(doc.documentId)}
                            >
                              <ExternalLink className="mr-1 h-3.5 w-3.5" />
                              {doc.label}
                            </Button>
                          </div>
                        ))}
                        <ChangeCheckLine
                          order={order}
                          onReorder={() => setConfirmReorder(order)}
                          reorderPending={reorderMutation.isPending}
                        />
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {showResult && !isDeleted && order.supportsChangeCheck ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          title="Check InfoTrack for new filings"
                          disabled={checkMutation.isPending}
                          onClick={() => checkMutation.mutate(order._id)}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {canRetry ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          title="Retry PDF download"
                          disabled={retryMutation.isPending}
                          onClick={() => retryMutation.mutate(order._id)}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {isDeleted ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          title="Restore search"
                          disabled={restoreMutation.isPending}
                          onClick={() => restoreMutation.mutate(order._id)}
                        >
                          <Undo2 className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          title="Delete from view"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setConfirmDelete(order)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete search from view?</DialogTitle>
            <DialogDescription>
              The search and its cost remain on the audit ledger — it will just
              be hidden from the default Search history view. Toggle "Show
              deleted" above the table to bring it back.
            </DialogDescription>
          </DialogHeader>
          {confirmDelete ? (
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {confirmDelete.displayName}
              </span>{" "}
              · {STATUS_LABELS[confirmDelete.status]}
            </p>
          ) : null}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (!confirmDelete) return;
                deleteMutation
                  .mutateAsync(confirmDelete._id)
                  .then(() => setConfirmDelete(null))
                  .catch(() => {
                    /* toast already shown */
                  });
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!confirmReorder}
        onOpenChange={(o) => !o && setConfirmReorder(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Re-order this search?</DialogTitle>
            <DialogDescription>
              Submits a new InfoTrack order using the same payload as the
              original search. The previous order stays in the history for
              audit; InfoTrack bills your firm for the new one.
            </DialogDescription>
          </DialogHeader>
          {confirmReorder ? (
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {confirmReorder.displayName}
              </span>
            </p>
          ) : null}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmReorder(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={reorderMutation.isPending}
              onClick={() => {
                if (!confirmReorder) return;
                reorderMutation
                  .mutateAsync(confirmReorder)
                  .then(() => setConfirmReorder(null))
                  .catch(() => {
                    /* toast already shown */
                  });
              }}
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              Submit new order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChangeCheckLine — small subtitle under "View document" that surfaces the
// most recent currency check. Three visual states:
//   • Never checked   → muted "Not yet checked for changes"
//   • Up to date      → muted "Up to date · checked Xh ago"
//   • New version     → amber "New version available" + Re-order link
//   • Unavailable     → muted "Currency check unavailable" with tooltip
// ---------------------------------------------------------------------------

interface ChangeCheckLineProps {
  order: OrderRow;
  onReorder: () => void;
  reorderPending: boolean;
}

function ChangeCheckLine({
  order,
  onReorder,
  reorderPending,
}: ChangeCheckLineProps) {
  const result = order.lastChangeCheckResult;
  const baselineMs = order.completedAt ?? order._creationTime;
  const purchasedAgo = formatRelative(baselineMs);

  // Product doesn't support change detection — there's no point offering
  // "Check for updates" or surfacing a "not yet checked" state. Lead with
  // the order age and a Re-order affordance instead.
  if (!order.supportsChangeCheck) {
    return (
      <div className="text-xs text-muted-foreground">
        Purchased {purchasedAgo} · automatic change detection not available for
        this product
      </div>
    );
  }

  if (!result) {
    return (
      <div className="text-xs text-muted-foreground">
        Purchased {purchasedAgo} · click the refresh icon to check for new
        filings
      </div>
    );
  }

  // Honest "we tried but couldn't get a definitive answer" state — surface
  // the technical detail in a popover so the practitioner can act on it
  // without having to ping engineering.
  if (result.changeCheckMethod === "unavailable") {
    return (
      <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
        <span>
          Purchased {purchasedAgo} · couldn&apos;t check for new filings (
          {formatRelative(result.checkedAt)})
        </span>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
            >
              Details
            </Button>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            className="w-80 space-y-2 text-xs"
          >
            <div className="font-medium text-foreground">
              Change check unavailable
            </div>
            <p className="whitespace-pre-line text-muted-foreground">
              {result.detail ??
                "InfoTrack didn't return a usable response. Re-order to refresh the data."}
            </p>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  if (result.changed) {
    const count = result.newDocumentCount ?? 0;
    const filingsLabel =
      count === 0
        ? "New filings detected"
        : `${count} new filing${count === 1 ? "" : "s"} since this extract`;
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md hover:opacity-90"
            >
              <Badge color="amber" variant="soft" className="gap-1">
                <Sparkles className="h-3 w-3" />
                {filingsLabel}
              </Badge>
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            className="w-80 space-y-2 text-xs"
          >
            <div className="font-medium text-foreground">
              {filingsLabel}
              {result.latestDocumentDate
                ? ` — most recent ${result.latestDocumentDate}`
                : null}
            </div>
            <p className="whitespace-pre-line text-muted-foreground">
              {result.detail ??
                "InfoTrack found new lodgements against this organisation since the extract was created."}
            </p>
          </PopoverContent>
        </Popover>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          disabled={reorderPending}
          onClick={onReorder}
        >
          Re-order
        </Button>
        <span className="text-xs text-muted-foreground">
          · checked {formatRelative(result.checkedAt)}
        </span>
      </div>
    );
  }

  return (
    <div className="text-xs text-muted-foreground">
      No new filings since this extract · checked{" "}
      {formatRelative(result.checkedAt)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ErrorPopover — small alert icon that opens a friendly explanation of an
// InfoTrack failure, with the raw payload tucked behind a disclosure for
// debugging. Keeps the table row at one line tall instead of forcing the
// whole row to grow to fit a 200-character JSON blob.
// ---------------------------------------------------------------------------

function ErrorPopover({ error }: { error: string }) {
  const parsed = useMemo(() => parseInfotrackError(error), [error]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-destructive hover:text-destructive"
          title="View error details"
        >
          <AlertCircle className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        className="w-80 space-y-3 text-sm"
      >
        <div className="space-y-1">
          <div className="font-medium text-destructive">{parsed.headline}</div>
          {parsed.bullets.length > 0 ? (
            <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
              {parsed.bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          ) : null}
        </div>
        {parsed.rawPayload ? (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer hover:text-foreground">
              Technical details
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto rounded-md bg-muted/50 p-2 font-mono text-[11px]">
              {parsed.rawPayload}
            </pre>
          </details>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
